import React, { useCallback, useState } from 'react';
import CalendarSelectApptTimeBtn from './CalendarSelectApptTimeBtn';
import css from './CalendarSelectAppt.module.scss';
import { ButtonGroup } from '@framework-controls/core';
import { ascCtaInteractionInput, ascElementConfigurationInput, rmTimeRec, SpecData, Transportation, VehicleRec } from 'src/models';
import SpecialConsiderationMessage from 'src/components/SpecialConsiderationMessage/SpecialConsiderationMessage';
import { convertTo24 } from 'src/helpers/TimeDisplayHelper/TimeDisplayHelper';
import { random } from 'lodash';
import { ascCtaInteraction, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import { sendSdEvent, getProviderId, buildApptData } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';
import { IAppointmentState } from 'src/store/states';

interface IProps {
    type: string;
    timeHandler: (time: string, transportation: Transportation) => void;
    apptTimes: rmTimeRec[];
    selectedTime: string;
    selectedType: string;
    transportation: Transportation;
    theme: string;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    appointment: IAppointmentState;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
}

const CalendarSelectApptTime = (props: IProps) => {
    const [specConsOpen, setSpecConsOpen] = useState(false);
    const [selectedAppt, setSelectedAppt] = useState('');

    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y' ||
        props.specData?.jaguarTrackingFlag === 'Y' ||
        props.specData?.landRoverTrackingFlag === 'Y';
    const isVW = props.specData?.vwTrackingFlag === 'Y';
    const closeSpecHandler = useCallback(() => {
        setSpecConsOpen(false);
    }, []);

    const proceedHandler = (time: string, transportation: Transportation) => {
        setSpecConsOpen(false);
        props.timeHandler(time, transportation);
    };

    const onTimeChange = (event: any, time: string, transportation: Transportation) => {
        // If the transportaion window has more info to show the user, open it here.
        const transInfo = transportation.information ?? '';
        if (transInfo !== '') {
            setSpecConsOpen(true);
            setSelectedAppt(time);
        } else {
            props.timeHandler(time, transportation);
        }
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: time,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, event, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: time,
            elementType: 'scheduling_tool',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, event);

        if (isSDAnalyticsEnabled) {
            if (!isVW){
                sendSdEvent('scheduleServiceApptStart', buildApptData(props.sessionId,
                    getProviderId(props.specData, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
                        props.lexusMCLaunched, props.lexusTDMCLaunched), props.appointment));
            }
            if (props.type) {
                sendSdEvent('scheduleServiceTransportStart', {
                    scheduleServiceSessionId: props.sessionId,
                    scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
                        props.lexusMCLaunched, props.lexusTDMCLaunched),
                    scheduleServiceTransportType: props.transportation.title,
                });
            }
        }
    };

    const displayTimes = props.apptTimes.map((time, index) => {
        let id = time.transFlag + index;
        if (time.transFlag === undefined) {
            id = ' ' + index + random(9999);
        }
        const selected = props.selectedType === time.transFlag && props.selectedTime === time.displayTime ?
            true :
            false;

        return (
            <CalendarSelectApptTimeBtn
                key={id}
                id={id}
                transportation={props.transportation}
                time={time.displayTime}
                label={time.displayTime}
                timeHandler={onTimeChange}
                selected={selected}
                theme={props.theme}
            />
        );
    });

    const sortedTimes = displayTimes.sort((a, b) => (convertTo24(a.props.time) > convertTo24(b.props.time)) ? 1 : -1);
    const divId = props.type + 'Times';

    return (
        <div id={divId} className={css.timesList}>
            <ButtonGroup variant='leftAlign'>
                {sortedTimes}
            </ButtonGroup>
            <SpecialConsiderationMessage
                isOpen={specConsOpen}
                selectedTime={selectedAppt}
                transportation={props.transportation}
                cancelHandler={closeSpecHandler}
                proceedHandler={proceedHandler}
                vehicles={props.vehicles}
                selectedVehicle={props.selectedVehicle}
                theme={props.theme}
            />
        </div>
    );
};

export default CalendarSelectApptTime;